import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardList, RefreshCw, Search, X } from 'lucide-react';
import { fetchShelfCountLog, SHELF_COUNT_LOG_LIMIT } from '../../supabase/services';
import { annotateCounts, summariseByPerson, isProblem, isBigDifference, personOf, signed } from './shelfCountLogUtils';

const PERIODS = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' }
];

const STATUS = {
  matched: { label: 'Matched', tone: 'good' },
  corrected: { label: 'Corrected', tone: 'warn' },
  recount: { label: 'Recount asked', tone: 'bad' },
  failed: { label: 'Failed', tone: 'bad' },
  skipped: { label: 'Skipped', tone: 'neutral' }
};

const formatWhen = (iso) => new Date(iso).toLocaleString('en-GB', {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
});

const daysAgo = (days) => new Date(Date.now() - days * 24 * 60 * 60 * 1000);
const units = (n) => `${n} ${Math.abs(n) === 1 ? 'unit' : 'units'}`;

const Pill = ({ tone, title, children }) => (
  <span className={`count-log-pill ${tone}`} title={title}>{children}</span>
);

// What the next count of the same part found, i.e. whether this count held up.
const NextCount = ({ row }) => {
  if (row.status === 'recount') {
    return row.abandoned
      ? <Pill tone="bad" title="No finished count followed within 30 minutes">No recount</Pill>
      : <span className="count-log-muted">Recounted</span>;
  }
  if (!row.next) return <span className="count-log-muted" title="Not counted again since">Not yet</span>;
  const diff = Number(row.next.difference) || 0;
  if (diff === 0) return <Pill tone="good">Held up</Pill>;
  return (
    <Pill
      tone="bad"
      title={`${personOf(row.next)} counted ${row.next.counted} on ${formatWhen(row.next.created_at)}, expected ${row.next.expected}`}
    >
      Off by {signed(diff)}
    </Pill>
  );
};

const PartCell = ({ row }) => (
  <div className="count-log-part" title={(row.serial_names || []).length ? `Work orders: ${row.serial_names.join(', ')}` : undefined}>
    <span className="count-log-part-desc">{row.part_description || row.part_name}</span>
    {row.part_description && <span className="count-log-part-code">{row.part_name}</span>}
  </div>
);

// Shelf counts taken when reporting frame / mount production: who counted what, and
// whether their count held up at the next count of the same part.
const ShelfCountLog = () => {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [person, setPerson] = useState('');
  const [part, setPart] = useState('');
  const [problemsOnly, setProblemsOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchShelfCountLog(daysAgo(days)).then(result => {
      if (cancelled) return;
      setLoading(false);
      setError(result.success ? '' : result.message);
      setRows(result.rows);
    });
    return () => { cancelled = true; };
  }, [days, reloadToken]);

  const annotated = useMemo(() => annotateCounts(rows), [rows]);
  const people = useMemo(() => summariseByPerson(annotated), [annotated]);

  const shown = useMemo(() => {
    const query = part.trim().toUpperCase();
    return annotated.filter(row => (!person || personOf(row) === person)
      && (!query || `${row.part_name} ${row.part_description || ''}`.toUpperCase().includes(query))
      && (!problemsOnly || isProblem(row)));
  }, [annotated, person, part, problemsOnly]);

  const totals = useMemo(() => {
    const settled = shown.filter(r => r.status === 'matched' || r.status === 'corrected');
    return {
      counts: settled.length,
      matched: settled.filter(r => r.status === 'matched').length,
      corrected: settled.filter(r => r.status === 'corrected').length,
      units: settled.reduce((n, r) => n + Math.abs(Number(r.difference) || 0), 0),
      problems: shown.filter(isProblem).length
    };
  }, [shown]);

  const filtered = Boolean(person || part.trim() || problemsOnly);
  const clearFilters = () => { setPerson(''); setPart(''); setProblemsOnly(false); };

  return (
    <section className="settings-card count-log" aria-label="Shelf count log">
      <div className="count-log-header">
        <h3 className="settings-section-title"><ClipboardList size={16} aria-hidden="true" /> Shelf count log</h3>
        <button
          type="button"
          className="count-log-refresh"
          onClick={() => setReloadToken(t => t + 1)}
          disabled={loading}
          aria-label="Reload the shelf count log"
          title="Reload"
        >
          <RefreshCw size={16} aria-hidden="true" />
        </button>
      </div>
      <p className="settings-row-help">
        A correction usually means an <strong>earlier</strong> report was miscounted, so each count is judged by the
        next count of the same part. &ldquo;Off by&rdquo; there points at the person who counted before.
      </p>

      <div className="count-log-filters">
        <label className="count-log-field">
          <span>Period</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {PERIODS.map(p => <option key={p.days} value={p.days}>{p.label}</option>)}
          </select>
        </label>
        <label className="count-log-field">
          <span>Person</span>
          <select value={person} onChange={(e) => setPerson(e.target.value)}>
            <option value="">Everyone</option>
            {people.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
          </select>
        </label>
        <label className="count-log-field count-log-search">
          <span>Part</span>
          <span className="count-log-search-box">
            <Search size={15} aria-hidden="true" />
            <input type="search" value={part} onChange={(e) => setPart(e.target.value)} placeholder="Part number or description" />
          </span>
        </label>
        <label className="count-log-toggle">
          <input type="checkbox" checked={problemsOnly} onChange={(e) => setProblemsOnly(e.target.checked)} />
          <span>Only problems</span>
        </label>
        {filtered && (
          <button type="button" className="count-log-clear" onClick={clearFilters}>
            <X size={14} aria-hidden="true" /> Clear filters
          </button>
        )}
      </div>

      {error && <p className="settings-error" role="alert">{error}</p>}
      {loading && <p className="count-log-muted">Loading...</p>}
      {!loading && !error && rows.length === 0 && <p className="count-log-empty">No shelf counts in this period.</p>}
      {!loading && rows.length >= SHELF_COUNT_LOG_LIMIT && (
        <p className="count-log-muted">Showing the latest {SHELF_COUNT_LOG_LIMIT} counts only. Pick a shorter period to see all of them.</p>
      )}

      {!loading && people.length > 0 && (
        <>
          <div className="count-log-stats" aria-label="Totals for the counts shown">
            <div className="count-log-stat"><span className="value">{totals.counts}</span><span className="label">Counts</span></div>
            <div className="count-log-stat good"><span className="value">{totals.matched}</span><span className="label">Matched</span></div>
            <div className="count-log-stat warn"><span className="value">{totals.corrected}</span><span className="label">Corrected</span></div>
            <div className="count-log-stat"><span className="value">{totals.units}</span><span className="label">Units corrected</span></div>
            <div className={`count-log-stat ${totals.problems ? 'bad' : ''}`}><span className="value">{totals.problems}</span><span className="label">Need a look</span></div>
          </div>

          <h4 className="count-log-subtitle">By person <span className="count-log-muted">Click a name to see only their counts</span></h4>
          <div className="count-log-table-wrap">
            <table className="count-log-table count-log-people" aria-label="Counts by person">
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col" className="num">Counts</th>
                  <th scope="col" className="num">Matched</th>
                  <th scope="col" className="num">Corrected</th>
                  <th scope="col" className="num" title="First counts so far off that a recount was asked for">Recounts</th>
                  <th scope="col" className="num" title="Of their counts that have been counted again since, how many the next count had to correct">Next count off</th>
                </tr>
              </thead>
              <tbody>
                {people.map(p => (
                  <tr
                    key={p.name}
                    className={person === p.name ? 'selected' : ''}
                    onClick={() => setPerson(person === p.name ? '' : p.name)}
                  >
                    <th scope="row">{p.name}</th>
                    <td className="num">{p.counts}</td>
                    <td className="num">{p.matched}</td>
                    <td className="num">{p.corrected}</td>
                    <td className="num">
                      {p.recounts}
                      {p.abandoned > 0 && <> <Pill tone="bad" title="Recounts asked for but never finished">{p.abandoned} unfinished</Pill></>}
                    </td>
                    <td className="num">
                      {p.checked === 0 ? <span className="count-log-muted">-</span> : (
                        <Pill tone={p.disputed > 0 ? 'bad' : 'good'}>
                          {p.disputed} of {p.checked}{p.disputed > 0 && ` (${units(p.disputedUnits)})`}
                        </Pill>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h4 className="count-log-subtitle">Counts ({shown.length})</h4>
          {shown.length === 0 ? (
            <p className="count-log-empty">No counts match these filters.</p>
          ) : (
            <div className="count-log-table-wrap">
              <table className="count-log-table count-log-counts" aria-label="Shelf counts">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Person</th>
                    <th scope="col">Product</th>
                    <th scope="col" className="num" title="Priority's stock, less anything scanned onto orders not shipped yet">Expected</th>
                    <th scope="col" className="num">Counted</th>
                    <th scope="col" className="num">Correction</th>
                    <th scope="col">Status</th>
                    <th scope="col">Next count</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(row => {
                    const st = STATUS[row.status] || { label: row.status, tone: 'neutral' };
                    return (
                      <tr key={row.id} className={isProblem(row) ? 'problem' : ''}>
                        <td data-label="When" className="when">{formatWhen(row.created_at)}</td>
                        <td data-label="Person">{personOf(row)}</td>
                        <td data-label="Product" className="product"><PartCell row={row} /></td>
                        <td data-label="Expected" className="num">{Number(row.expected)}</td>
                        <td data-label="Counted" className="num">
                          {row.counted}
                          {row.first_count !== null && row.status !== 'recount' && (
                            <span className="count-log-muted" title="First count, before the recount"> (first {row.first_count})</span>
                          )}
                        </td>
                        <td data-label="Correction" className={`num ${isBigDifference(row.difference, row.expected, row.counted) ? 'count-log-big' : ''}`}>
                          {Number(row.difference) === 0 ? '-' : signed(row.difference)}
                        </td>
                        <td data-label="Status">
                          <Pill tone={st.tone} title={row.error_message || (row.doc_no ? `Priority document ${row.doc_no}` : undefined)}>{st.label}</Pill>
                        </td>
                        <td data-label="Next count"><NextCount row={row} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default ShelfCountLog;
